import React, { useState, useRef, useCallback, useEffect } from 'react';
import styled from 'styled-components';
import Colors from '../../../../ui/global/Colors';
import Fonts from '../../../../ui/global/Fonts';
import GameHistory from './GameHistory';
import Button from '../../../../ui/components/Button';
import { GithubFormat } from '../../utils/formatCode';
import InputSimple from '../../../../ui/components/InputSimple';
import GameActionsMenu from './GameActionsMenu';
import { useStreamUIState } from '../stream/helpers/streamStore';
import InputWithMentions, { InputWithMentionsRef } from './InputWithMentions';
import { Mention } from './MentionPill';
import Tooltip from '../../../../ui/components/Tooltip';
import { useCreditsContext } from '../../../pricing/CreditsContext';
import { GameHistoryEntry } from '../../../../backend/useGame';

const InstructionContainer = styled.div`
    position: relative;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    font-family: ${Fonts.lexendMedium.fontFamily};
    font-weight: ${Fonts.lexendMedium.fontWeight};
    font-size: 16px;
    line-height: 1.2em;
    border-radius: 14px;
    background-color: ${Colors.grey9};
    border: 1px solid ${Colors.grey8};
    padding: 6px;
    color: ${Colors.white};
    gap: 2px;
    transition: border 0.2s ease;

    &:hover {
        border: 1px solid ${Colors.grey7};
    }
`;

interface GameInputProps {
    config: any;
    history: Partial<GameHistoryEntry>[];
    onHistorySelect: (entry: Partial<GameHistoryEntry>) => void;
    selectedHistoryId: string;
    instruction: string;
    handleInstructionChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
    handleGenerateFromInput: (instruction: string) => void;
    isGeneratingName: boolean;
    isGeneratingGame: boolean;
    isUpdatingGameArt: boolean;
}

const GameInput: React.FC<GameInputProps> = ({ config, history, onHistorySelect, selectedHistoryId, instruction, handleInstructionChange, handleGenerateFromInput, isGeneratingName, isGeneratingGame, isUpdatingGameArt }) => {
    const [mentionAnchorEl, setMentionAnchorEl] = useState<HTMLElement | null>(null);
    const [isInMentionMode, setIsInMentionMode] = useState(false);
    const [mentionType, setMentionType] = useState<string | null>(null);
    const [mentionFilter, setMentionFilter] = useState('');
    const [mentions, setMentions] = useState<Mention[]>([]);
    const [stopDisabled, setStopDisabled] = useState(false);
    const inputContainerRef = useRef<HTMLDivElement>(null);
    const tempAnchorRef = useRef<HTMLElement | null>(null);
    const inputWithMentionsRef = useRef<InputWithMentionsRef>(null);
    const { isOutOfCredits } = useCreditsContext();
    const { isStreaming } = useStreamUIState();

    // Clean up temporary anchor element when component unmounts
    useEffect(() => {
        return () => {
            if (tempAnchorRef.current && tempAnchorRef.current.parentNode) {
                tempAnchorRef.current.parentNode.removeChild(tempAnchorRef.current);
            }
        };
    }, []);

    // Handle @ key press to open actions menu
    const handleAtMenu = (open: boolean, atPosition: { x: number, y: number, width: number, height: number } | null) => {
        if (open) {
            // console.log('Mention context changed, position:', atPosition);
            
            // If position is null, we should hide the menu
            if (!atPosition) {
                // Close the mention menu
                setIsInMentionMode(false);
                setMentionAnchorEl(null);
                setMentionType(null);
                setMentionFilter('');
                
                // Remove the temporary anchor element
                if (tempAnchorRef.current && tempAnchorRef.current.parentNode) {
                    tempAnchorRef.current.parentNode.removeChild(tempAnchorRef.current);
                    tempAnchorRef.current = null;
                }
                
                return;
            }
            
            if (inputContainerRef.current) {
                // If we're already in mention mode, just update the filter and return
                // This prevents recreating the anchor element while the menu is open
                if (isInMentionMode && tempAnchorRef.current) {
                    // Menu is already open, don't recreate the anchor element
                    // This prevents the menu from flickering as the user types
                    return;
                }
                
                // Clean up any existing temporary anchor only if we're creating a new one
                if (tempAnchorRef.current && tempAnchorRef.current.parentNode) {
                    tempAnchorRef.current.parentNode.removeChild(tempAnchorRef.current);
                }
                
                // Create a temporary element to serve as anchor for the menu
                const tempAnchor = document.createElement('div');
                tempAnchor.style.position = 'absolute';
                
                // Position the menu at the @ symbol location, not at the cursor
                tempAnchor.style.left = `${atPosition.x - 2}px`;
                tempAnchor.style.top = `${atPosition.y - atPosition.height - 10}px`;
                tempAnchor.style.height = `${atPosition.height}px`;
                tempAnchor.style.width = `${atPosition.width + 4}px`;
                tempAnchor.style.pointerEvents = 'none';
                tempAnchor.style.zIndex = '1000';
                // tempAnchor.style.backgroundColor = 'red'; tempAnchor.style.opacity = '0.5'; // debug
                
                // Store reference to the temporary anchor
                tempAnchorRef.current = tempAnchor;
                
                // Append to the DOM
                document.body.appendChild(tempAnchor);
                
                // Set the anchor element for the menu
                setMentionAnchorEl(tempAnchor);
                
                // Enter mention mode
                setIsInMentionMode(true);
                
                // console.log('Mention mode activated, anchor element created');
            }
        } else {
            handleCloseMentionMenu();
        }
    };

    // Handle action selection (e.g., Art, Sound) from menu
    const handleActionSelected = (actionType: string) => {
        // console.log('Action selected:', actionType);
        setMentionType(actionType.toLowerCase());
        setMentionFilter('');
        // console.log(`Action selected: ${actionType}`);
        // Insert the type specifier into the input
        if (inputWithMentionsRef.current && 'insertTypeSpecifier' in inputWithMentionsRef.current) {
            (inputWithMentionsRef.current as any).insertTypeSpecifier(actionType);
      }
    };

    // Handle detail item selection (e.g., specific art piece)
    const handleDetailSelected = (id: string, image: string, name: string) => {
        // console.log('Detail selected:', id, name);
        if (mentionType) {
            // Create a new mention
            const newMention: Mention = {
                id,
                type: mentionType,
                name: name,
            };
            
            // Add mention to the list
            setMentions(prev => [...prev, newMention]);
            
            // Insert the mention using the ref method
            if (inputWithMentionsRef.current) {
                inputWithMentionsRef.current.insertMention(newMention);
            }
            
            // Close the mention menu
            setIsInMentionMode(false);
            setMentionAnchorEl(null);
            setMentionType(null);
            setMentionFilter('');
            
            // Remove the temporary anchor element
            if (tempAnchorRef.current && tempAnchorRef.current.parentNode) {
                tempAnchorRef.current.parentNode.removeChild(tempAnchorRef.current);
                tempAnchorRef.current = null;
            }
        }
    };

    // Handle instruction change with mentions
    const handleInputValueChange = useCallback((value: string, newMentions: Mention[]) => {
        setMentions(newMentions);
        
        // Create a synthetic event to maintain compatibility with the existing handleInstructionChange
        const syntheticEvent = {
            target: { value },
            currentTarget: { value },
            preventDefault: () => {},
            stopPropagation: () => {}
        } as React.ChangeEvent<HTMLInputElement>;
        
        handleInstructionChange(syntheticEvent);
        
        // The mention filter and type are now handled by the InputWithMentions component
        // We just need to update our local state based on the text
        if (isInMentionMode) {
            if (mentionType) {
                // Extract the text after @type: to use as filter for selected type
                const match = value.match(new RegExp(`@${mentionType}:(\\S*)`));
                if (match && match[1]) {
                    setMentionFilter(match[1]);
                } else {
                    setMentionFilter('');
                }
            } else {
                // Extract text after @ to filter the actions menu
                const match = value.match(/@([^:\s]*)/);
                if (match && match[1]) {
                    setMentionFilter(match[1]);
                } else {
                    setMentionFilter('');
                }
            }
        }
    }, [handleInstructionChange, isInMentionMode, mentionType]);

    // Handle mention creation
    const handleMentionCreated = (mention: Mention) => {
        // console.log('Mention created:', mention);
        setMentions(prev => [...prev, mention]);
    };

    // Handle mention removal
    const handleMentionRemoved = (mention: Mention) => {
        // console.log('Mention removed:', mention);
        setMentions(prev => prev.filter(m => m.id !== mention.id));
    };

    // Handle mention click
    const handleMentionClicked = (mention: Mention) => {
        // console.log('Mention clicked:', mention);
        // Implement any click behavior here
    };

    // Close mention menu
    const handleCloseMentionMenu = () => {
        setIsInMentionMode(false);
        setMentionAnchorEl(null);
        setMentionType(null);
        setMentionFilter('');
        
        // Remove the temporary anchor element
        if (tempAnchorRef.current && tempAnchorRef.current.parentNode) {
            tempAnchorRef.current.parentNode.removeChild(tempAnchorRef.current);
            tempAnchorRef.current = null;
        }
    };

    return (
        <InstructionContainer className="instruction-container" ref={inputContainerRef}>
            <GameHistory 
                history={history}
                onHistorySelect={onHistorySelect}
                selectedHistoryId={selectedHistoryId}
            />
            {config.GAME_ASSETS_INTEGRATION.input ? (
                <>
                <GameActionsMenu 
                    isInMentionMode={isInMentionMode}
                    mentionAnchorEl={mentionAnchorEl}
                    onArtSelected={handleDetailSelected}
                    mentionFilter={mentionFilter}
                    onActionSelected={handleActionSelected}
                    hideButtonMenu={true}
                />
                <InputWithMentions
                    ref={inputWithMentionsRef}
                    value={instruction}
                    onChange={handleInputValueChange}
                    placeholder="Make a change..."
                    colorMode="dark"
                    onAtMenu={handleAtMenu}
                    onMentionCreated={handleMentionCreated}
                    onMentionRemoved={handleMentionRemoved}
                    onMentionClicked={handleMentionClicked}
                    onCurrentMentionChange={(currentMention, type, filter) => {
                        // console.log('Current mention changed:', currentMention, type, filter);
                        if (currentMention === null) {
                            // Mention context cleared
                            setIsInMentionMode(false);
                            setMentionType(null);
                            setMentionFilter('');
                        } else if (type) {
                            // Type specified (e.g., @art:)
                            setIsInMentionMode(true);
                            setMentionType(type);
                            setMentionFilter(filter);
                        } else {
                            // Just @ without type
                            setIsInMentionMode(true);
                            setMentionType(null);
                            setMentionFilter(filter);
                        }
                    }}
                    // onEnterPress={() => {
                        // if (instruction.trim() !== '') {
                        //     handleGenerateFromInput(instruction);
                        // }
                    // }}
                />
                </>
            ) : (
                <InputSimple
                    placeholder="Make a change..." 
                    value={instruction} 
                    onChange={handleInstructionChange} 
                    // onEnterPress={() => {
                        // if (instruction.trim() !== '') {
                        //     handleGenerateFromInput(instruction);
                        // }
                    // }}
                    textAlign="left"
                    colorMode='dark'
                />
            )}
            {/* <Tooltip text='Edit Game' sub_text='1 credit' colorMode="dark" > */}
                {config.ENABLE_STREAM_CANCEL && isStreaming ? (
                    <Button
                        size="tiny"
                        color="white"
                        icon="stop"
                        onClick={() => {
                            if (stopDisabled) return;
                            setStopDisabled(true);
                            // try { cancelCurrentRun(); } catch (_) {}
                        }}
                        disabled={stopDisabled || isGeneratingName || isOutOfCredits.game_edit_limited || isUpdatingGameArt}
                    />
                ) : (
                    <Button 
                        size="tiny" 
                        color="white" 
                        icon="send" 
                        onClick={() => handleGenerateFromInput(instruction)} 
                        disabled={instruction === '' || isGeneratingName || isGeneratingGame || isOutOfCredits.game_edit_limited || isUpdatingGameArt}
                    />
                )}
            {/* </Tooltip> */}
        </InstructionContainer>
    );
};

export default GameInput;